'use client'

import * as React from 'react'
import { useState } from 'react'
import { Editor } from '@tiptap/react'
import { ArrowUp, Bot, Sparkles } from 'lucide-react'
import { Streamdown } from 'streamdown'
import { useTiptapEditor } from '@/hooks/use-tiptap-editor'
import { Button, Command, CommandInput, ScrollArea } from '@deepguide-ai/ui'
import { AICompletionCommands } from './ai-completion-commands'
import { AISelectorCommands } from './ai-selector-commands'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

export type AICompletionResolver = (
  prompt: string,
  editorContent: string,
  selection: string,
  selectedText: string,
  inputValue: string
) => Promise<string | ReadableStream<Uint8Array<ArrayBufferLike>>>

export interface EditorContext {
  editorContent: string
  selection: string
  selectedText: string
  prompt: string
  activityContext?: {
    action?: string
    blockId?: string
    claimId?: string
    quoteText?: string
    currentClaimText?: string
  }
  openOnly?: boolean
}

export interface AISelectorProps {
  editor?: Editor | null
  open: boolean
  onOpenChange: (open: boolean) => void
  completeResolver?: AICompletionResolver
  onContinueInChat?: (context: EditorContext) => void
  mode?: 'selection' | 'ask' // 'selection' = text selected, 'ask' = /ask command
}

export function AISelector({
  editor: providedEditor,
  onOpenChange,
  completeResolver,
  onContinueInChat,
}: AISelectorProps) {
  const { editor } = useTiptapEditor(providedEditor)
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [completion, setCompletion] = useState('')
  const [currentCommand, setCurrentCommand] = useState<string>('')
  const { enabled: isChatEnabled } = useFeatureFlag('chat')

  const hasCompletion = typeof completion === 'string' && completion.length > 0

  // Simulate async AI completion (replace with your real API call)
  const fakeCompleteResolver = async (prompt: string) => {
    // For now, just echo the prompt as markdown
    return new Promise<string>((resolve) => {
      setTimeout(() => {
        resolve(`**AI Response:**\n\n${prompt}`)
      }, 1200)
    })
  }

  const handleCustomCommand = async (commandName: string = 'custom') => {
    if (!editor) return
    if (!inputValue.trim() && ['custom'].includes(commandName)) {
      // must have input to continue
      return
    }

    // Store the current command to know how to handle completion
    setCurrentCommand(commandName)

    // Execute the completion resolver for all commands (including custom)
    setIsLoading(true)
    try {
      const editorContent = editor.getJSON()
      const selection = editor.state.selection
      const slice = editor.state.selection.content()
      const selectedText = editor.storage.markdown.serializer.serialize(
        slice.content
      )

      // Get surrounding context for better AI suggestions
      const { from, to } = selection
      const doc = editor.state.doc

      // Get text before selection (up to 500 chars)
      const beforeStart = Math.max(0, from - 500)
      const beforeSlice = doc.slice(beforeStart, from)
      const textBefore = editor.storage.markdown.serializer.serialize(
        beforeSlice.content
      )

      // Get text after selection (up to 500 chars)
      const afterEnd = Math.min(doc.content.size, to + 500)
      const afterSlice = doc.slice(to, afterEnd)
      const textAfter = editor.storage.markdown.serializer.serialize(
        afterSlice.content
      )

      const result =
        (await completeResolver?.(
          commandName,
          JSON.stringify(editorContent),
          JSON.stringify(selection),
          selectedText,
          inputValue,
          textBefore,
          textAfter
        )) ?? (await fakeCompleteResolver(inputValue))
      if (typeof result === 'string') {
        setCompletion(result)
      } else {
        // Stream chunks in real-time for better UX
        const reader = result.getReader()
        let chunks = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          // Convert Uint8Array to string and append
          const newChunk = new TextDecoder().decode(value)
          chunks += newChunk
          // Update completion on each chunk for streaming markdown rendering
          setCompletion(chunks)
        }
      }

      // Clear input after getting completion so Command doesn't filter completion actions
      setInputValue('')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePresetCommand = async (commandName: string) => {
    return handleCustomCommand(commandName)
  }

  const handleDiscard = () => {
    setCompletion('')
    setInputValue('')
    onOpenChange(false)
  }

  if (!editor) return null

  return (
    <Command className='w-[400px] max-w-[90vw] border shadow-lg' shouldFilter={!hasCompletion}>
      {hasCompletion ? (
        <div className='mb-2 flex max-h-[300px] border-b border-[var(--border)]'>
          <ScrollArea className='w-full'>
            <div className='prose prose-sm max-w-none p-4'>
              <Streamdown>{completion}</Streamdown>
            </div>
          </ScrollArea>
        </div>
      ) : null}

      {isLoading ? (
        <div className='text-muted-foreground flex h-12 w-full items-center px-4 text-sm font-medium'>
          <Bot className='text-primary mr-2 h-4 w-4 shrink-0 animate-pulse' />
          AI is thinking
          <div className='ml-1.5 flex items-center justify-center gap-1 pt-[4px]'>
            <div className='bg-primary h-[5px] w-[5px] animate-bounce rounded-full [animation-delay:0s]' />
            <div className='bg-primary h-[5px] w-[5px] animate-bounce rounded-full [animation-delay:0.2s]' />
            <div className='bg-primary h-[5px] w-[5px] animate-bounce rounded-full [animation-delay:0.4s]' />
          </div>
        </div>
      ) : null}

      {!isLoading && !hasCompletion && (
        <React.Fragment>
          <div className='relative mt-2 border-b border-[var(--border)] pb-2'>
            <CommandInput
              value={inputValue}
              onValueChange={setInputValue}
              autoFocus
              placeholder='Ask AI to edit or generate...'
              className='pr-10'
              icon={
                <div className='text-primary shrink-0'>
                  <Sparkles className='h-4 w-4' />
                </div>
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && inputValue.trim()) {
                  e.preventDefault()
                  handleCustomCommand('custom')
                }
              }}
            />
            <Button
              size='sm'
              className='absolute top-[18px] right-2 h-6 w-6 -translate-y-1/2 rounded-full p-0'
              onClick={() => {
                handleCustomCommand('custom')
              }}
              disabled={!inputValue.trim()}
            >
              <ArrowUp className='h-4 w-4' />
            </Button>
          </div>

          <AISelectorCommands
            editor={editor}
            onSelect={handlePresetCommand}
            onOpenChat={
              isChatEnabled && onContinueInChat
                ? () => {
                    const editorContent = editor.getJSON()
                    const selection = editor.state.selection
                    const slice = editor.state.selection.content()
                    const selectedText =
                      editor.storage.markdown.serializer.serialize(
                        slice.content
                      )
                    // Open chat with selected text context, but don't auto-send
                    onContinueInChat({
                      editorContent: JSON.stringify(editorContent),
                      selection: JSON.stringify(selection),
                      selectedText,
                      prompt: '', // No prompt for "Open Chat" - just context
                    })
                    setInputValue('')
                    onOpenChange(false)
                  }
                : undefined
            }
          />
        </React.Fragment>
      )}

      {!isLoading && hasCompletion && (
        <AICompletionCommands
          completion={completion}
          isAppendMode={currentCommand === 'complete'}
          onAccept={() => {
                const { from, to } = editor.state.selection

                // Parse markdown to ProseMirror nodes
                let nodes = undefined
                if (editor.storage.markdown && editor.storage.markdown.parser) {
                  const parsedDoc =
                    editor.storage.markdown.parser.parse(completion)
                  nodes = parsedDoc?.content // Get the content nodes
                }

                // For "complete" command, append after selection instead of replacing
                if (currentCommand === 'complete') {
                  editor.commands.command(({ tr }) => {
                    // Insert content at the end of selection (append)
                    if (nodes) {
                      tr.insert(to, nodes)
                    } else {
                      tr.insertText(completion, to)
                    }
                    return true
                  })
                } else {
                  // For other commands, replace selection
                  editor.commands.command(({ tr }) => {
                    // Delete current selection
                    if (from !== to) {
                      tr.delete(from, to)
                    }

                    // Insert new content
                    if (nodes) {
                      tr.replaceWith(from, from, nodes)
                    } else {
                      tr.insertText(completion, from)
                    }

                    return true
                  })
                }

                setCompletion('')
                setInputValue('')
                setCurrentCommand('')
                onOpenChange(false)
                editor.commands.focus()
              }}
              onInsertBelow={() => {
                // Insert AI completion below selection (supports undo)
                const { to } = editor.state.selection

                // Parse markdown to ProseMirror nodes
                let nodes = undefined
                if (editor.storage.markdown && editor.storage.markdown.parser) {
                  const parsedDoc =
                    editor.storage.markdown.parser.parse(completion)
                  nodes = parsedDoc?.content // Get the content nodes
                }

                // Use command() for proper transaction and undo support
                editor.commands.command(({ tr }) => {
                  // Insert content at position after selection
                  if (nodes) {
                    tr.insert(to, nodes)
                  } else {
                    tr.insertText(completion, to)
                  }

                  return true
                })

                setCompletion('')
                setInputValue('')
                onOpenChange(false)
                editor.commands.focus()
              }}
          onDiscard={handleDiscard}
        />
      )}
    </Command>
  )
}
